import React, { useState, useCallback, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FaComments, FaSearch, FaTimes } from "react-icons/fa";
import {
  useConversations,
  useDeleteConversation,
  useUpdateConversation,
} from "@/api/conversation";
import { useResourceGallery } from "@/hooks/use-resource-gallery";
import { conversationGalleryConfig } from "@/config/resource-gallery-configs";
import { useMultiSelect } from "@/hooks/use-multi-select";
import { optimisticMutation, optimisticListMutation, urlPredicate } from "@/utils/optimistic";
import ConversationItem from "./conversation-item";
import logger from "@/utils/logger";

interface ConversationsSectionProps {
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

/**
 * Conversations section for the Arc sidebar.
 * Displays all conversations when on the /chat route.
 * Uses the unified useResourceGallery hook for consistent behavior.
 */
const ConversationsSection: React.FC<ConversationsSectionProps> = ({
  onMultiSelectContextMenu,
}) => {
  const queryClient = useQueryClient();
  const { data: conversations = [], refetch } = useConversations();
  const deleteConversationMutation = useDeleteConversation();
  const updateConversationMutation = useUpdateConversation();
  const convPredicate = urlPredicate("conversations");

  // Multi-select hook
  const { isSelected } = useMultiSelect();

  // Use the shared resource gallery hook for search, filtering, and grouping
  const {
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    filteredItems: filteredConversations,
    orderedIds: orderedConversationIds,
  } = useResourceGallery(conversations, conversationGalleryConfig);

  // Rename state
  const [renamingId, setRenamingId] = useState<string | null>(null);

  // Search state
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    searchInputRef.current?.focus();
  }, [setSearchQuery]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + F to focus search
      if ((e.metaKey || e.ctrlKey) && e.key === "f" && conversations.length > 0) {
        if (searchInputRef.current) {
          e.preventDefault();
          searchInputRef.current.focus();
        }
      }
      // Escape to clear search
      if (e.key === "Escape" && isSearchFocused) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSearchFocused, conversations.length, setSearchQuery]);

  const handleDeleteConversation = async (conversationId: string) => {
    try {
      await optimisticListMutation({
        queryClient,
        queryPredicate: convPredicate,
        operation: { type: "remove", id: conversationId },
        mutationFn: () =>
          deleteConversationMutation.mutateAsync({ uriParams: { id: conversationId } }),
        onSettled: () => refetch(),
      });
    } catch (error) {
      logger.error("Failed to delete conversation:", error);
    }
  };

  const handleRenameConversation = async (
    conversationId: string,
    newName: string,
  ) => {
    try {
      await optimisticMutation({
        queryClient,
        queryPredicate: convPredicate,
        updater: (convos: any[]) =>
          convos.map((c: any) =>
            c.id === conversationId ? { ...c, name: newName } : c,
          ),
        mutationFn: () =>
          updateConversationMutation.mutateAsync({
            uriParams: { id: conversationId },
            name: newName,
          }),
        onSettled: () => refetch(),
      });
      setRenamingId(null);
    } catch (error) {
      logger.error("Failed to rename conversation:", error);
    }
  };

  if (conversations.length === 0) {
    return (
      <div className="px-3 py-2">
        {/* Section Title */}
        <div className="flex items-center gap-2 px-2 mb-2 text-[var(--arc-accent)]">
          <FaComments size={10} />
          <span className="text-[10px] font-semibold uppercase tracking-wider">
            Conversations
          </span>
          <div className="flex-1 h-px bg-[var(--arc-sidebar-border-accent)]" />
        </div>

        {/* Empty state */}
        <div className="px-2 py-4 text-center">
          <p className="text-[var(--arc-sidebar-text-secondary)] text-xs">
            Aucune conversation
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      {/* Search Bar */}
      {conversations.length > 0 && (
        <div className="px-1 mb-3">
          <div
            className={`relative flex items-center rounded-md transition-all duration-200 ${
              isSearchFocused
                ? "bg-[var(--arc-sidebar-accent-glow)] ring-1 ring-[var(--arc-accent)]"
                : "bg-[var(--arc-sidebar-accent-glow)] hover:bg-[rgba(var(--arc-accent-rgb),0.15)]"
            }`}
          >
            <FaSearch
              className={`absolute left-2.5 transition-colors duration-200 ${
                isSearchFocused ? "arc-sidebar-icon-active" : ""
              }`}
              size={11}
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              placeholder="Search..."
              className="w-full bg-transparent text-[var(--arc-sidebar-text-primary)] text-xs py-1.5 pl-7 pr-7 placeholder-[var(--arc-sidebar-text-secondary)] focus:outline-none focus:ring-0"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 p-0.5 rounded hover:bg-[var(--arc-sidebar-accent-glow)] transition-colors text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)]"
              >
                <FaTimes size={10} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Section Title */}
      <div className="flex items-center gap-2 px-2 mb-2 text-[var(--arc-accent)]">
        <FaComments size={10} />
        <span className="text-[10px] font-semibold uppercase tracking-wider">
          Conversations
        </span>
        <div className="flex-1 h-px bg-[var(--arc-sidebar-border-accent)]" />
      </div>

      {/* Conversations List */}
      {filteredConversations.length > 0 ? (
        <div className="space-y-0.5">
          {filteredConversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              onDelete={() => handleDeleteConversation(conversation.id!)}
              isRenaming={renamingId === conversation.id}
              onRenameSubmit={(newName) =>
                handleRenameConversation(conversation.id!, newName)
              }
              onRenameCancel={() => setRenamingId(null)}
              isSelected={isSelected(conversation.id!)}
              orderedIds={orderedConversationIds}
              onMultiSelectContextMenu={onMultiSelectContextMenu}
            />
          ))}
        </div>
      ) : searchQuery ? (
        <div className="px-2 py-3 text-center text-[var(--arc-sidebar-text-secondary)]">
          <span className="text-xs">Aucun résultat</span>
        </div>
      ) : null}
    </div>
  );
};

ConversationsSection.displayName = "ConversationsSection";

export default ConversationsSection;
